import { type FormEvent, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAppStore, type LobbySeat } from "@/store/useAppStore";
import { ensureJoined, getRoom, leaveSession } from "@/client/session";
import { MAX_PLAYERS, MIN_PLAYERS } from "@/shared/match";

export const Route = createFileRoute("/lobby/$roomId")({
  component: LobbyRoute,
});

function LobbyRoute() {
  const { roomId } = Route.useParams();
  const pseudo = useAppStore((state) => state.pseudo);

  // L'URL est partageable : on arrive ici sans identité dès qu'un ami envoie
  // le lien. Le pseudo persiste en localStorage, mais la première visite ne
  // peut pas en avoir — d'où le formulaire en repli, plutôt qu'un renvoi à
  // l'accueil qui ferait perdre le roomId.
  if (!pseudo) return <PseudoGate roomId={roomId} />;

  // Monté seulement une fois l'identité connue : l'effet de join n'a donc
  // jamais à être conditionnel.
  return <Lobby roomId={roomId} pseudo={pseudo} />;
}

function PseudoGate({ roomId }: { roomId: string }) {
  const setPseudo = useAppStore((state) => state.setPseudo);
  const [pseudoInput, setPseudoInput] = useState("");
  const trimmed = pseudoInput.trim();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmed) setPseudo(trimmed);
  }

  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-6 px-4 text-center">
      <h1 className="font-heading text-2xl font-bold tracking-tight">On t'attend</h1>
      <p className="text-sm text-muted-foreground">
        Partie <span className="font-mono">{roomId}</span>
      </p>
      <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
        <Input
          value={pseudoInput}
          onChange={(event) => setPseudoInput(event.target.value)}
          placeholder="Pseudo"
          aria-label="Pseudo"
          maxLength={16}
          autoFocus
        />
        <Button type="submit" disabled={!trimmed}>
          Entrer dans le dojo
        </Button>
      </form>
    </main>
  );
}

function Lobby({ roomId, pseudo }: { roomId: string; pseudo: string }) {
  const navigate = useNavigate();
  const seats = useAppStore((state) => state.seats);
  const phase = useAppStore((state) => state.phase);
  const countdown = useAppStore((state) => state.countdown);
  const sessionId = useAppStore((state) => state.sessionId);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Idempotent : déjà dans cette Room (on vient de la créer), on ne rejoint
    // pas une deuxième fois.
    ensureJoined(roomId, pseudo).catch((err) => {
      console.warn("[session] join impossible", err);
      setError("Cette partie n'existe plus, est pleine, ou a déjà commencé.");
    });
  }, [roomId, pseudo]);

  useEffect(() => {
    // C'est le serveur qui décide du départ, pas le bouton : tous les clients
    // basculent sur le même changement de phase.
    if (phase === "playing") navigate({ to: "/play" });
  }, [phase, navigate]);

  async function handleQuit() {
    await leaveSession();
    navigate({ to: "/" });
  }

  function handleCopy() {
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (error) {
    return (
      <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-6 px-4 text-center">
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
        <Button onClick={() => navigate({ to: "/" })}>Retour à l'accueil</Button>
      </main>
    );
  }

  const enoughNinjas = seats.length >= MIN_PLAYERS;
  const starting = phase === "starting";

  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-8 px-4 text-center">
      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Dojo</h1>
        <button
          type="button"
          onClick={handleCopy}
          className="font-mono text-sm text-muted-foreground underline-offset-4 hover:underline"
          title="Copier le lien d'invitation"
        >
          {copied ? "lien copié !" : roomId}
        </button>
      </header>

      <div className="flex flex-col items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Équipe ({seats.length}/{MAX_PLAYERS})
        </span>
        <ul className="flex justify-center gap-2">
          {Array.from({ length: MAX_PLAYERS }, (_, index) => (
            <TeamSlot
              key={index}
              seat={seats.find((seat) => seat.index === index) ?? null}
              isLocal={seats.some((seat) => seat.index === index && seat.id === sessionId)}
            />
          ))}
        </ul>
      </div>

      {starting ? (
        <p className="font-heading text-5xl font-bold tabular-nums" aria-live="polite">
          {countdown}
        </p>
      ) : (
        <Button
          className="w-full"
          disabled={!enoughNinjas}
          onClick={() => getRoom()?.send("start")}
        >
          {enoughNinjas ? "Lancer la partie" : `En attente (${MIN_PLAYERS} ninjas minimum)`}
        </Button>
      )}

      <Button variant="ghost" size="sm" onClick={handleQuit}>
        Quitter
      </Button>
    </main>
  );
}

function TeamSlot({ seat, isLocal }: { seat: LobbySeat | null; isLocal: boolean }) {
  return (
    <li
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-lg border text-xs font-medium",
        seat
          ? "border-primary bg-primary/10 text-foreground"
          : "border-dashed border-muted-foreground/40 text-muted-foreground",
        isLocal && "ring-2 ring-primary",
      )}
      title={seat ? (isLocal ? `${seat.name} (toi)` : seat.name) : "En attente"}
    >
      {seat ? seat.name.slice(0, 2).toUpperCase() : "…"}
    </li>
  );
}
